import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useDisclaimer } from '@/features/legal/hooks/useDisclaimer';
import { PageSkeleton } from '@/components/PageSkeleton';

// Token-bearing routes the same branch of App.tsx mounts. Bouncing them to
// /disclaimer would drop the ?token the user clicked, so they pass regardless.
const EXEMPT_PATHS = new Set(['/reset-password', '/verify-email-change']);

/**
 * DisclaimerGuard — DISCLAIMER-01
 *
 * CA-01: holds Property Managers and Board Members on /disclaimer until they
 * accept the active version. EX-01 and EX-02 need no branch here — the backend
 * reports `needsAcceptance` for both.
 */
export function DisclaimerGuard({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { data, isLoading } = useDisclaimer();

  // A failed request leaves this false on purpose: an unreachable legal API
  // must not lock every manager out of the portal.
  const needsAcceptance = !isLoading && data?.needsAcceptance === true;
  const mustRedirect =
    needsAcceptance && location !== '/disclaimer' && !EXEMPT_PATHS.has(location);

  useEffect(() => {
    if (mustRedirect) {
      setLocation('/disclaimer');
    }
  }, [mustRedirect, setLocation]);

  if (isLoading || mustRedirect) {
    return <PageSkeleton />;
  }

  return <>{children}</>;
}
